import { useState, type ReactNode } from 'react';
import { Link, useNavigate, useParams } from 'react-router';

import { BasketBar } from '@/shared/quotes/AddToQuote';
import { useCan, useShowsPurchase } from '@/shared/session/profiles';
import {
  RETRYABLE_FAILURES,
  STATUS_LABELS,
  type TariffItem,
  tariffDate,
  tariffFailureTitle,
  type Version,
  WORKING,
} from '@/shared/tariffs/types';
import {
  useDiscard,
  useOriginal,
  useRetry,
  useVersion,
  useVersionItems,
} from '@/shared/tariffs/useTariffs';
import { Badge, type Tone } from '@/shared/ui/Badge';
import { Button } from '@/shared/ui/Button';
import { Callout } from '@/shared/ui/Callout';
import { Card } from '@/shared/ui/Card';
import { Dialog } from '@/shared/ui/Dialog';
import { EmptyState } from '@/shared/ui/EmptyState';
import { ErrorMessage } from '@/shared/ui/ErrorMessage';
import { ArrowLeftIcon, DownloadIcon, FileTextIcon } from '@/shared/ui/icons';
import { PageHeader } from '@/shared/ui/PageHeader';
import { Spinner } from '@/shared/ui/Spinner';
import { formatBytes, formatDate } from '@/shared/utils/format';

import { ArticleDialog, ArticlesTable } from './Articles';
import { ColumnsStep } from './ColumnsStep';
import { DraftReview } from './DraftReview';

const PAGE_SIZE = 50;
const COUNT = new Intl.NumberFormat('es-ES');
const dayTime = new Intl.DateTimeFormat('es-ES', {
  day: '2-digit',
  month: '2-digit',
  year: 'numeric',
  hour: '2-digit',
  minute: '2-digit',
});

const STATUS_TONES: Record<Version['status'], Tone> = {
  preparing: 'info',
  needs_columns: 'warn',
  reading: 'info',
  draft: 'warn',
  published: 'ok',
  superseded: 'neutral',
  discarded: 'neutral',
  failed: 'danger',
};

/**
 * One version of a tariff (contract v10.48), from the upload to the history. The screen
 * follows the version: what the worker is doing while it has it, the columns while they
 * wait for a person, what changes and «Publicar» once read, the articles once published.
 * Everybody reads a published one; the rest is for whoever manages the documents (the API
 * answers 404 to anybody else).
 */
export function VersionPage() {
  const { versionId } = useParams();
  const version = useVersion(versionId);

  const back = (to: string, label: string) => (
    <Link to={to} className="text-ink-3 hover:text-ink inline-flex items-center gap-1">
      <ArrowLeftIcon size={14} /> {label}
    </Link>
  );

  if (version.isPending) return <Spinner variant="block" label="Cargando la tarifa" />;
  if (version.isError) {
    return (
      <>
        <PageHeader title="Tarifa" back={back('/documentos', 'Documentos')} />
        <ErrorMessage error={version.error}>No se pudo cargar la tarifa</ErrorMessage>
      </>
    );
  }
  return (
    <VersionScreen
      version={version.data}
      back={back(`/documentos/proveedores/${version.data.supplier_id}`, version.data.supplier_name)}
    />
  );
}

function VersionScreen({ version, back }: { version: Version; back: ReactNode }) {
  const navigate = useNavigate();
  const manage = useCan('manage_documents');
  const purchase = useShowsPurchase();
  const original = useOriginal();
  const [changingColumns, setChangingColumns] = useState(false);
  const [discarding, setDiscarding] = useState(false);
  const { status } = version;
  const working = WORKING.has(status);
  // Changing the columns is for a draft or a failed reading; once the reading is asked for
  // (`onRead`) or the version moves on, the screen follows the version again.
  const columnsOpen =
    version.columns &&
    (status === 'needs_columns' ||
      (changingColumns && (status === 'draft' || status === 'failed')));

  const subtitle = [
    version.filename,
    version.size ? formatBytes(version.size) : null,
    version.created_by ? `subida por ${version.created_by}` : null,
    formatDate(dayTime, version.created_at),
  ]
    .filter(Boolean)
    .join(' · ');

  return (
    <>
      <PageHeader
        back={back}
        title={version.price_list_name}
        badge={
          <Badge tone={version.stuck ? 'danger' : STATUS_TONES[status]}>
            {version.stuck ? 'Atascada' : STATUS_LABELS[status]}
          </Badge>
        }
        description={subtitle}
        actions={
          purchase ? (
            <Button
              variant="secondary"
              icon={<DownloadIcon size={16} />}
              disabled={original.isPending}
              onClick={() =>
                original.mutate(version.id, {
                  onSuccess: (url) => {
                    window.open(url, '_blank', 'noopener');
                  },
                })
              }
            >
              Original
            </Button>
          ) : undefined
        }
      />
      {original.isError ? (
        <ErrorMessage error={original.error}>No se pudo descargar el original</ErrorMessage>
      ) : null}
      {version.email_id ? (
        <p className="text-ink-3 -mt-2 text-[13px]">
          Llegó por correo.{' '}
          <Link className="link" to={`/correos/${version.email_id}`}>
            Ver el correo
          </Link>
        </p>
      ) : null}

      {working ? <Working version={version} /> : null}

      {status === 'failed' ? (
        <Failed version={version} onChooseColumns={() => setChangingColumns(true)} />
      ) : null}

      {columnsOpen && version.columns ? (
        <ColumnsStep
          version={version}
          columns={version.columns}
          onRead={() => setChangingColumns(false)}
          {...(status === 'needs_columns' ? {} : { onCancel: () => setChangingColumns(false) })}
        />
      ) : null}
      {status === 'needs_columns' && !version.columns ? (
        <Callout tone="warn">
          Esta tarifa espera a que alguien elija las columnas, y eso lo hace quien gestiona los
          documentos con permiso para ver la compra.
        </Callout>
      ) : null}

      {status === 'draft' && !columnsOpen ? (
        <DraftReview
          version={version}
          {...(version.columns ? { onChangeColumns: () => setChangingColumns(true) } : {})}
        />
      ) : null}

      {status === 'published' ? (
        <Callout tone="ok" compact>
          Vigente: el buscador usa esta versión. Tarifa del {tariffDate(version.tariff_date)}
          {version.published_at
            ? `, publicada el ${formatDate(dayTime, version.published_at)}`
            : ''}
          {version.published_by ? ` por ${version.published_by}` : ''}.
        </Callout>
      ) : null}
      {status === 'superseded' ? (
        <Callout tone="info" compact>
          Sustituida por una versión más nueva: se guarda en el historial, pero el buscador ya no la
          usa.
        </Callout>
      ) : null}
      {status === 'discarded' ? (
        <Callout tone="info" compact>
          Descartada sin publicar. El fichero original se guarda.
        </Callout>
      ) : null}

      {/* A stuck one too: a file that brings the worker down sticks at every attempt, and
          while it is open no other version of the tariff can be uploaded. */}
      {manage && (['needs_columns', 'draft', 'failed'].includes(status) || version.stuck) ? (
        <div>
          <Button variant="ghost" size="sm" onClick={() => setDiscarding(true)}>
            Descartar esta versión
          </Button>
        </div>
      ) : null}
      {discarding ? (
        <DiscardDialog
          version={version}
          onClose={() => setDiscarding(false)}
          onDiscarded={() => void navigate(`/documentos/proveedores/${version.supplier_id}`)}
        />
      ) : null}

      {['draft', 'published', 'superseded'].includes(status) ? <Items version={version} /> : null}
    </>
  );
}

function Working({ version }: { version: Version }) {
  const manage = useCan('manage_documents');
  const retry = useRetry(version.id);
  const label =
    version.status === 'preparing'
      ? 'Mirando el fichero y sus columnas…'
      : 'Leyendo los artículos…';
  if (version.stuck) {
    return (
      <Callout
        tone="warn"
        title="Lleva demasiado tiempo"
        action={
          manage ? (
            <Button size="sm" disabled={retry.isPending} onClick={() => retry.mutate()}>
              {retry.isPending ? 'Pidiendo…' : 'Volver a pedirlo'}
            </Button>
          ) : undefined
        }
      >
        La lectura no ha terminado. Puede que el sistema se reiniciara a medias: pídela otra vez.
        {retry.isError ? (
          <ErrorMessage error={retry.error}>No se pudo pedir otra vez</ErrorMessage>
        ) : null}
      </Callout>
    );
  }
  return (
    <Card>
      <Spinner label={label} />
      <p className="text-ink-3 mt-2 text-[13px]">
        Una tarifa grande tarda unos segundos. Puedes salir de esta pantalla: sigue sola.
      </p>
    </Card>
  );
}

function Failed({ version, onChooseColumns }: { version: Version; onChooseColumns: () => void }) {
  const manage = useCan('manage_documents');
  const retry = useRetry(version.id);
  const askAgain = RETRYABLE_FAILURES.has(version.error_code ?? '');
  return (
    <Callout
      tone="danger"
      title={tariffFailureTitle(version.error_code)}
      action={
        manage ? (
          askAgain ? (
            <Button size="sm" disabled={retry.isPending} onClick={() => retry.mutate()}>
              {retry.isPending ? 'Pidiendo…' : 'Volver a pedirla'}
            </Button>
          ) : version.columns ? (
            <Button size="sm" variant="secondary" onClick={onChooseColumns}>
              Elegir las columnas
            </Button>
          ) : undefined
        ) : undefined
      }
    >
      {version.error ?? 'No se pudo leer el fichero.'}{' '}
      {version.error_code ? (
        <span className="text-ink-3 text-[12.5px]">({version.error_code})</span>
      ) : null}
      {retry.isError ? (
        <ErrorMessage error={retry.error}>No se pudo pedir otra vez</ErrorMessage>
      ) : null}
    </Callout>
  );
}

function DiscardDialog({
  version,
  onClose,
  onDiscarded,
}: {
  version: Version;
  onClose: () => void;
  onDiscarded: () => void;
}) {
  const discard = useDiscard(version.id);
  return (
    <Dialog
      title="¿Descartar esta versión?"
      description="Se quitan los artículos leídos. La versión vigente no cambia y el fichero original se guarda."
      size="sm"
      onClose={onClose}
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={discard.isPending} data-autofocus>
            No
          </Button>
          <Button
            variant="danger"
            disabled={discard.isPending}
            onClick={() => discard.mutate(undefined, { onSuccess: onDiscarded })}
          >
            {discard.isPending ? 'Descartando…' : 'Descartar'}
          </Button>
        </>
      }
    >
      {discard.isError ? (
        <ErrorMessage error={discard.error}>No se pudo descartar</ErrorMessage>
      ) : (
        <p className="text-ink-2 text-[14px]">{version.filename}</p>
      )}
    </Dialog>
  );
}

function Items({ version }: { version: Version }) {
  const [page, setPage] = useState(0);
  const [open, setOpen] = useState<TariffItem | null>(null);
  const items = useVersionItems(version.id, page * PAGE_SIZE);
  const rows = items.data?.items ?? [];
  const total = items.data?.total ?? 0;

  return (
    <section aria-label="Artículos" className="flex flex-col gap-3">
      <h2 className="text-ink text-[15px] font-semibold">
        Artículos{total ? ` (${COUNT.format(total)})` : ''}
      </h2>
      {items.isPending ? <Spinner variant="block" label="Cargando artículos" /> : null}
      {items.isError ? (
        <ErrorMessage error={items.error}>No se pudieron cargar los artículos</ErrorMessage>
      ) : null}
      {items.data && rows.length === 0 ? (
        <EmptyState icon={<FileTextIcon size={24} />} title="Ningún artículo leído" />
      ) : null}
      {rows.length > 0 && version.status === 'published' ? <BasketBar /> : null}
      {rows.length > 0 ? (
        <ArticlesTable
          addable={version.status === 'published'}
          items={rows}
          label="Artículos de la tarifa"
          onOpen={setOpen}
        />
      ) : null}
      {total > PAGE_SIZE ? (
        <div className="text-ink-3 flex items-center justify-end gap-3 text-[13px]">
          <span>
            {page * PAGE_SIZE + 1}–{page * PAGE_SIZE + rows.length} de {COUNT.format(total)}
          </span>
          <Button
            variant="secondary"
            size="sm"
            disabled={page === 0}
            onClick={() => setPage((current) => Math.max(0, current - 1))}
          >
            Anteriores
          </Button>
          <Button
            variant="secondary"
            size="sm"
            disabled={(page + 1) * PAGE_SIZE >= total}
            onClick={() => setPage((current) => current + 1)}
          >
            Siguientes
          </Button>
        </div>
      ) : null}
      {open ? (
        <ArticleDialog
          addable={version.status === 'published'}
          item={open}
          onClose={() => setOpen(null)}
        />
      ) : null}
    </section>
  );
}
